import { Dialog } from "@kobalte/core/dialog"
import { Component, For, Show, createEffect, createSignal } from "solid-js"
import { alertDialogState, dismissAlertDialog } from "../stores/alerts"
import type { AlertVariant, AlertDialogState } from "../stores/alerts"
import { useI18n } from "../lib/i18n"
import { authRecovery } from "../lib/auth-recovery"

const variantAccent: Record<AlertVariant, { badgeBg: string; badgeBorder: string; badgeText: string; symbol: string }> = {
  info: {
    badgeBg: "var(--badge-neutral-bg)",
    badgeBorder: "var(--border-base)",
    badgeText: "var(--accent-primary)",
    symbol: "i",
  },
  warning: {
    badgeBg: "rgba(255, 152, 0, 0.14)",
    badgeBorder: "var(--status-warning)",
    badgeText: "var(--status-warning)",
    symbol: "!",
  },
  error: {
    badgeBg: "var(--danger-soft-bg)",
    badgeBorder: "var(--status-error)",
    badgeText: "var(--status-error)",
    symbol: "!",
  },
}

function dismiss(confirmed: boolean, payload?: AlertDialogState | null, promptValue?: string) {
  const current = payload ?? alertDialogState()

  if (current?.type === "choice") {
    // Only an explicit choice button resolves a value; every dismissal cancels.
    current.resolveChoice?.(confirmed ? promptValue ?? null : null)
    dismissAlertDialog()
    return
  }

  if (current?.type === "confirm") {
    if (confirmed) {
      current.onConfirm?.()
    } else {
      current.onCancel?.()
    }
    current.resolve?.(confirmed)
    dismissAlertDialog()
    return
  }

  if (current?.type === "prompt") {
    if (confirmed) {
      current.onConfirm?.()
      current.resolvePrompt?.(promptValue ?? "")
    } else {
      current.onCancel?.()
      current.resolvePrompt?.(null)
    }
    dismissAlertDialog()
    return
  }

  if (confirmed) {
    current?.onConfirm?.()
  }
  dismissAlertDialog()
}

const AlertDialog: Component = () => {
  const { t } = useI18n()
  let primaryButtonRef: HTMLButtonElement | undefined
  let promptInputRef: HTMLInputElement | undefined

  createEffect(() => {
    const state = alertDialogState()
    if (!state || authRecovery.required()) return

    // Other dialogs take their initial focus in onOpenAutoFocus below.
    if (state.type !== "prompt") return
    queueMicrotask(() => {
      if (authRecovery.required() || alertDialogState() !== state) return
      promptInputRef?.focus()
      promptInputRef?.select()
    })
  })

  return (
    <Show when={alertDialogState()} keyed>
      {(payload) => {
        const variant = payload.variant ?? "info"
        const accent = variantAccent[variant]

        const fallbackTitle =
          variant === "warning"
            ? t("alertDialog.fallbackTitle.warning")
            : variant === "error"
              ? t("alertDialog.fallbackTitle.error")
              : t("alertDialog.fallbackTitle.info")

        const title = payload.title || fallbackTitle
        const isConfirm = payload.type === "confirm"
        const isPrompt = payload.type === "prompt"
        const confirmLabel =
          payload.confirmLabel ||
          (isConfirm
            ? t("alertDialog.actions.confirm")
            : isPrompt
              ? t("alertDialog.actions.run")
              : t("alertDialog.actions.ok"))
        const cancelLabel = payload.cancelLabel || t("alertDialog.actions.cancel")

        const [inputValue, setInputValue] = createSignal(payload.inputDefaultValue ?? "")

        return (
          <Dialog
            open={!authRecovery.required()}
            modal
            onOpenChange={(open) => {
              // Only handle dismiss if dialog is dismissible (default: true)
              if (!open && payload.dismissible !== false) {
                dismiss(false, payload)
              }
            }}
          >
            <Dialog.Portal>
              <Dialog.Overlay class="modal-overlay z-[60]" />
              <Dialog.Content
                class="alert-dialog modal-surface fixed left-1/2 top-1/2 z-[1310] w-full max-w-sm -translate-x-1/2 -translate-y-1/2 p-6 border border-base shadow-2xl"
                tabIndex={-1}
                onOpenAutoFocus={(event) => {
                  // Confirms keep Kobalte's default focus on Cancel, so Enter never
                  // confirms a destructive action; the focus ring shows it from the
                  // start (styles/components/alert-dialog.css). Choice dialogs focus
                  // their last, non-destructive choice instead.
                  if (payload.type !== "choice" || !primaryButtonRef) return
                  event.preventDefault()
                  primaryButtonRef.focus()
                }}
              >
                   <div class="flex items-start gap-3">
                     <div
                       class="flex h-12 w-12 shrink-0 items-center justify-center border text-base font-semibold"
                       style={{
                         "background-color": accent.badgeBg,
                         "border-color": accent.badgeBorder,
                         color: accent.badgeText,
                       }}
                       aria-hidden
                     >
                       {accent.symbol}
                     </div>
                     <div class="flex-1 min-w-0">
                       <Dialog.Title class="text-lg font-semibold text-primary">{title}</Dialog.Title>
                       <Dialog.Description class="text-sm text-secondary mt-1 whitespace-pre-line break-words">
                         {payload.message}
                         {payload.detail && <p class="mt-2 text-secondary">{payload.detail}</p>}
                       </Dialog.Description>
                     </div>
                   </div>

                    <Show when={isPrompt}>
                      <div class="mt-4">
                        <label for="prompt-input" class="text-sm font-medium text-secondary">
                          {payload.inputLabel || t("alertDialog.prompt.inputLabel")}
                        </label>
                        <input
                          id="prompt-input"
                          ref={(el) => {
                            promptInputRef = el
                          }}
                          class="form-input mt-2"
                          value={inputValue()}
                          placeholder={payload.inputPlaceholder || ""}
                          autocapitalize="off"
                          autocorrect="off"
                          spellcheck={false}
                          onInput={(e) => setInputValue(e.currentTarget.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault()
                              dismiss(true, payload, inputValue())
                            }
                          }}
                        />
                      </div>
                    </Show>

                   <Show when={payload.type === "choice"}>
                     <div class="mt-6 flex flex-wrap justify-end gap-3">
                       <button type="button" class="button-secondary" onClick={() => dismiss(false, payload)}>
                         {cancelLabel}
                       </button>
                       <For each={payload.choices ?? []}>
                         {(choice, index) => (
                           <button
                             type="button"
                             class={choice.tone === "danger" ? "button-danger" : choice.tone === "primary" ? "button-primary" : "button-secondary"}
                             ref={(el) => {
                               if (index() === (payload.choices?.length ?? 0) - 1) primaryButtonRef = el
                             }}
                             onClick={() => dismiss(true, payload, choice.value)}
                           >
                             {choice.label}
                           </button>
                         )}
                       </For>
                     </div>
                   </Show>

                   <Show when={payload.type !== "choice"}>
                   <div class="mt-6 flex justify-end gap-3">
                     {(isConfirm || isPrompt) && (
                       <button
                         type="button"
                         class="button-secondary"
                         onClick={() => dismiss(false, payload)}
                       >
                         {cancelLabel}
                       </button>
                     )}
                     <button
                       type="button"
                       class="button-primary"
                       onClick={() => dismiss(true, payload, inputValue())}
                     >
                       {confirmLabel}
                     </button>
                    </div>
                   </Show>
                  </Dialog.Content>
                </Dialog.Portal>
              </Dialog>
         )
       }}
     </Show>
   )
 }

export default AlertDialog
